/**
 * UserBudgetEditor — inline editor for a single user's monthly Eva cap (SF-3825).
 *
 * The company-detail "All Users" roster shows each user's spend and "% of cap",
 * where the cap is now the user's EFFECTIVE per-user cap (override or default).
 * This control lets an authorized operator set/clear that per-user override
 * directly from the row, mirroring {@link CompanyBudgetControl} (the per-company
 * equivalent) but compact enough to live inside a table cell:
 *   - shows the current effective cap,
 *   - an editable numeric input + a Save button that only appears once the value
 *     differs from the loaded value,
 *   - a "Revert" action that clears the override (sends null) when one is set.
 *
 * Per the ticket, roster rows do NOT click through to the admin users table, so
 * this editor is the only per-user write affordance in the view.
 *
 * Permission-aware: writes require `admin:eva:budget:write`. Read-only operators
 * see a disabled input + tooltip; a server-side 403 (defence in depth) surfaces
 * a clear error toast. On a successful save the parent refetches the roster so
 * the row's "% of cap" (whose denominator is this effective cap) updates.
 *
 * Lazy-read: the GET for a row's override only fires once the operator actually
 * opens the editor (`active`), so rendering a 50-row page does not fan out into
 * 50 budget requests. Until then the row shows the effective cap already carried
 * on the roster payload.
 */
import { useEffect, useMemo, useState } from 'react';
import { usePermissions } from '@refinedev/core';
import { Button, InputNumber, Space, Tooltip, Typography, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import type { AxiosError } from 'axios';

import { hasPermission } from '../../../utils/permissions';
import { useCanEditCompany } from '../../companies/api/editableCompaniesClient';
import { formatCurrency, EM_DASH, parseMoney } from '../../../utils/format';
import {
  EVA_BUDGET_READ_PERMISSION,
  EVA_BUDGET_WRITE_PERMISSION,
  useUserBudgetOverride,
  useSetUserBudgetOverride,
  userBudgetUpdateMutationArgs,
  type EvaUserBudgetOverride,
} from '../api/evaUserBudgetClient';

const { Text } = Typography;

export interface UserBudgetEditorProps {
  /** User ObjectId whose monthly cap is being edited. */
  userId: string;
  /**
   * SF-4668: the company the user belongs to. Staff with a company list may only change budgets in
   * companies on it. Omitted = treated as not editable (fails closed).
   */
  companyId?: string;
  /**
   * Effective cap already carried on the roster row (USD string). Shown as the
   * resting value so the row needs no request until the editor is opened.
   */
  effectiveCapUsd: string | null;
  /** Whether a per-user override drives the roster row's effective cap. */
  isOverride: boolean;
  /** Gate requests on access-control resolution (defaults to true). */
  enabled?: boolean;
  /** Called after a successful save/revert so the parent can refetch the roster. */
  onSaved?: () => void;
}

/** Pull a human-readable message out of an sf-api axios error (incl. 403). */
function errorMessage(error: unknown, fallback: string): string {
  const axiosErr = error as AxiosError<{ message?: string }> | undefined;
  if (axiosErr?.response?.status === 403) {
    return 'You do not have permission to change this user\u2019s Eva budget.';
  }
  return axiosErr?.response?.data?.message || fallback;
}

/** Inline-editable monthly Eva budget control for a single user (table row). */
export const UserBudgetEditor: React.FC<UserBudgetEditorProps> = ({
  userId,
  companyId,
  effectiveCapUsd,
  isOverride: rowIsOverride,
  enabled = true,
  onSaved,
}) => {
  const { data: permissions, isLoading: permsLoading } = usePermissions<string[]>();
  const perms = permissions ?? [];
  const canRead = hasPermission(perms, EVA_BUDGET_READ_PERMISSION);
  const { canEditCompany } = useCanEditCompany(companyId);
  const canWrite = hasPermission(perms, EVA_BUDGET_WRITE_PERMISSION) && canEditCompany;

  // Lazy: stay collapsed (and fire NO request) until the operator opens the row.
  const [open, setOpen] = useState(false);

  // Only fire the GET once the editor is open AND the operator can read budgets.
  const budgetQuery = useUserBudgetOverride(userId, {
    enabled: enabled && open && !permsLoading && canRead,
  });
  const budget: EvaUserBudgetOverride | undefined = budgetQuery.data?.data;
  const setBudget = useSetUserBudgetOverride();

  // Prefer the freshly-fetched override once it lands; until then fall back to
  // the effective cap carried on the roster row so the resting display is correct.
  const loadedCap = budget ? parseMoney(budget.effectiveCapUsd) : parseMoney(effectiveCapUsd);
  const isOverride = budget ? budget.isOverride === true : rowIsOverride;

  const [value, setValue] = useState<number | null>(loadedCap);

  // Re-seed the input from the authoritative value whenever fresh override data
  // lands (open → fetch, or post-save refetch). Critically, ALSO re-seed from
  // the roster-carried effective cap while the editor is OPEN but the GET has
  // not yet resolved (budget === undefined): otherwise a roster poll that
  // updates `effectiveCapUsd` is reflected in `loadedCap` but not in `value`,
  // leaving the editor dirty against a stale `value` — clicking Save then would
  // write back the outdated cap and silently revert another operator's change.
  useEffect(() => {
    if (budget) {
      setValue(parseMoney(budget.effectiveCapUsd));
    } else if (open) {
      setValue(parseMoney(effectiveCapUsd));
    }
  }, [budget, open, effectiveCapUsd]);

  // The override GET must have resolved before we trust a Save: until `budget`
  // lands we only have the roster-carried cap, and writing on top of that races
  // a concurrent change. `budgetReady` gates the Save affordance accordingly.
  const budgetReady = !budgetQuery.isLoading && budget !== undefined;

  const isDirty = useMemo(() => value !== loadedCap, [value, loadedCap]);
  const saving = setBudget.isLoading;

  const handleSave = () => {
    if (!budgetReady) {
      message.error('Still loading this user\u2019s current budget — try again in a moment.');
      return;
    }
    if (value === null || !Number.isFinite(value) || value < 0) {
      message.error('Enter a non-negative monthly cap, or revert to default.');
      return;
    }
    setBudget.mutate(userBudgetUpdateMutationArgs(userId, { monthlyCostLimitUsd: value }), {
      onSuccess: () => {
        message.success('User Eva budget updated.');
        budgetQuery.refetch?.();
        onSaved?.();
      },
      onError: (error) => message.error(errorMessage(error, 'Failed to update the Eva budget.')),
    });
  };

  const handleRevert = () => {
    setBudget.mutate(userBudgetUpdateMutationArgs(userId, { monthlyCostLimitUsd: null }), {
      onSuccess: () => {
        message.success('Reverted to the default Eva budget.');
        budgetQuery.refetch?.();
        onSaved?.();
      },
      onError: (error) => message.error(errorMessage(error, 'Failed to revert the Eva budget.')),
    });
  };

  // Collapsed resting state: show the effective cap + an Edit affordance.
  if (!open) {
    const capLabel = loadedCap !== null ? formatCurrency(loadedCap) : EM_DASH;
    return (
      <Space size={4}>
        <Text>{capLabel}</Text>
        {isOverride ? <Text type="secondary" style={{ fontSize: 11 }}>(override)</Text> : null}
        {canWrite ? (
          <Tooltip title="Edit this user's monthly Eva budget">
            <Button
              type="text"
              size="small"
              aria-label="Edit user Eva budget"
              icon={<EditOutlined />}
              onClick={() => setOpen(true)}
            />
          </Tooltip>
        ) : null}
      </Space>
    );
  }

  // Expanded editor. Mirrors CompanyBudgetControl's affordances inline.
  const writeDisabledHint = canWrite
    ? undefined
    : !canEditCompany
      ? 'This company is not on your list of companies, so you can only view it.'
      : 'You do not have permission to change the Eva budget (admin:eva:budget:write).';

  return (
    <Space size={4} align="center" wrap>
      <Tooltip title={writeDisabledHint}>
        <InputNumber
          aria-label="User monthly Eva budget (USD)"
          value={value}
          min={0}
          step={5}
          disabled={!canWrite || saving || budgetQuery.isLoading}
          prefix="$"
          size="small"
          style={{ width: 110 }}
          onChange={(next) => setValue(typeof next === 'number' ? next : null)}
        />
      </Tooltip>
      {canWrite && isDirty && budgetReady && (
        <Button type="primary" size="small" loading={saving} onClick={handleSave}>
          Save
        </Button>
      )}
      {canWrite && isOverride && (
        <Button danger size="small" loading={saving} onClick={handleRevert}>
          Revert
        </Button>
      )}
      <Button type="text" size="small" disabled={saving} onClick={() => setOpen(false)}>
        Close
      </Button>
    </Space>
  );
};
